<script>
import { Article, ArticleTag, Label } from '@/api'
import PageFormBase from '@/crudBases/form/PageFormBase'
import RevisionsMixin from '@/crudBases/form/RevisionsMixin'
import moment from 'moment-timezone'
import fields from '@/components/fields/'
import { get } from 'lodash'

const EDITORIAL_TIME_ZONE = 'Europe/Warsaw'
const PUBLICATION_AT_FORMAT = 'YYYY-MM-DDTHH:mm:ss'
const LEGACY_PUBLICATION_DATE_FORMAT = 'YYYY-MM-DD'

export default {
  extends: PageFormBase,
  data() {
    return {
      livePreview: true,
      tagOptions: [],
      redirectRoute: { name: 'Article List' },
      arrayFields: ['images'],
      publicationAtReady: false,
      previewLinkLoading: false
    }
  },
  created() {
    this.fetchTags()
    if (this.form.publication_at === undefined) {
      this.$set(
        this.form,
        'publication_at',
        moment().tz(EDITORIAL_TIME_ZONE).format(PUBLICATION_AT_FORMAT)
      )
    }
    this.$nextTick(() => {
      this.publicationAtReady = true
    })
  },
  watch: {
    'form.publication_at'(value) {
      if (!this.publicationAtReady || !value) {
        return
      }
      const publicationAt = moment.tz(
        value,
        PUBLICATION_AT_FORMAT,
        true,
        EDITORIAL_TIME_ZONE
      )
      if (!publicationAt.isValid()) {
        return
      }
      const isScheduled = publicationAt.isAfter(moment())
      this.$set(this.form, 'is_publication_scheduled', isScheduled)
      if (isScheduled) {
        this.$set(this.form, 'is_hidden', true)
      }
    }
  },
  methods: {
    getNavButtons() {
      return [
        ...PageFormBase.methods.getNavButtons.call(this),
        this.getPreviewLinkButton()
      ]
    },
    getPreviewLinkButton() {
      return {
        component: 'el-button',
        attrs: {
          type: 'info',
          loading: this.previewLinkLoading
        },
        listeners: {
          click: this.openPreviewLink
        },
        innerText: 'Wygeneruj link podglądu'
      }
    },
    openPreviewLink() {
      const previewWindow = window.open('', '_blank')
      if (!previewWindow) {
        this.$message({
          message: 'Przeglądarka zablokowała otwarcie podglądu w nowej karcie.',
          type: 'error'
        })
        return
      }

      previewWindow.opener = null
      this.previewLinkLoading = true
      this.errors = {}
      this.service
        .previewLink(this.lookupParams, this.getSavePayload())
        .then(({ data }) => {
          previewWindow.location.replace(data.url)
        })
        .catch(error => {
          previewWindow.close()
          this.errorHandler(error)
        })
        .finally(() => {
          this.previewLinkLoading = false
        })
    },
    async instanceFetchCallback() {
      this.publicationAtReady = false
      this.normalizePublicationAt()
      try {
        await this.fetchAndLoadPendingRevision()
        this.normalizePublicationAt()
      } finally {
        this.$nextTick(() => {
          this.publicationAtReady = true
        })
      }
    },
    normalizePublicationAt() {
      if (this.form.publication_at) {
        this.$set(
          this.form,
          'publication_at',
          moment(this.form.publication_at)
            .tz(EDITORIAL_TIME_ZONE)
            .format(PUBLICATION_AT_FORMAT)
        )
      } else if (this.form.publication_date) {
        const publicationAt = moment.tz(
          this.form.publication_date,
          LEGACY_PUBLICATION_DATE_FORMAT,
          true,
          EDITORIAL_TIME_ZONE
        )
        if (publicationAt.isValid()) {
          const isScheduled = publicationAt.isAfter(moment())
          this.$set(
            this.form,
            'publication_at',
            publicationAt.format(PUBLICATION_AT_FORMAT)
          )
          this.$set(this.form, 'is_publication_scheduled', isScheduled)
          if (isScheduled) {
            this.$set(this.form, 'is_hidden', true)
          }
          this.$delete(this.form, 'publication_date')
        }
      }
    },
    loadRevision(revision) {
      const publicationAtWasReady = this.publicationAtReady
      this.publicationAtReady = false
      RevisionsMixin.methods.loadRevision.call(this, revision)
      this.normalizePublicationAt()
      this.$nextTick(() => {
        this.publicationAtReady = publicationAtWasReady
      })
    },
    getPreparedPayload(payload) {
      if (payload.is_publication_scheduled) {
        delete payload.is_hidden
      }
      if (payload.publication_at) {
        payload.publication_at = moment
          .tz(
            payload.publication_at,
            PUBLICATION_AT_FORMAT,
            true,
            EDITORIAL_TIME_ZONE
          )
          .format()
      }
      return payload
    },
    fetchTags() {
      const params = { page_size: 'max' }
      ArticleTag.fetch({}, { params }).then(res => {
        this.tagOptions = res.data.results.map(item => ({
          label: item.name,
          value: item.name
        }))
      })
    },
    getGeneralFields() {
      return [
        new fields.Input({
          label: 'Tytuł',
          modelPath: [this, 'form', 'title'],
          required: true
        }),
        new fields.ForeignKey({
          label: 'Etykieta',
          modelPath: [this, 'form', 'label'],
          labelField: 'name',
          clearable: true,
          service: Label
        }),
        new fields.Date({
          label: 'Data i godzina publikacji',
          type: 'datetime',
          format: 'yyyy-MM-dd HH:mm',
          valueFormat: 'yyyy-MM-ddTHH:mm:ss',
          modelPath: [this, 'form', 'publication_at'],
          required: true
        }),
        new fields.Select({
          label: 'Tagi',
          modelPath: [this, 'form', 'tags'],
          multiple: true,
          filterable: true,
          allowCreate: true,
          options: this.tagOptions
        }),
        new fields.Input({
          label: 'Zewnętrzny URL',
          modelPath: [this, 'form', 'external_url']
        }),
        new fields.Input({
          label: 'Tekst przy liście artykułów',
          modelPath: [this, 'form', 'show_article_text'],
          help: "Domyślnie: 'Zobacz artykuł'",
          condition:
            process.env.VUE_APP_IS_INNOVATION &&
            JSON.parse(process.env.VUE_APP_IS_INNOVATION)
        }),
        new fields.Switch({
          label: 'Ukryta',
          modelPath: [this, 'form', 'is_hidden'],
          disabled: this.publicationIsScheduled
        }),
        new fields.Switch({
          label: 'Czarno-białe zdjęcia',
          modelPath: [this, 'form', 'is_black_and_white']
        }),
        new fields.Switch({
          label: 'Artykuł przypięty',
          modelPath: [this, 'form', 'is_pinned']
        }),
        {
          component: 'h3',
          innerText: 'Zdjęcia'
        },
        new fields.Formset({
          modelPath: [this, 'form', 'images'],
          items: this.form.images.map((item, i) => ({
            headerLabel: get(item, ['title']),
            fields: [
              new fields.Image({
                label: 'Zdjęcie',
                required: true,
                modelPath: [this, 'form', 'images', i]
              })
            ]
          }))
        })
      ]
    }
  },
  computed: {
    publicationIsScheduled() {
      return !!this.form.is_publication_scheduled
    },
    service() {
      return Article
    }
  }
}
</script>
